import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { blinkService } from '../services/blinkService';

const { FiX, FiShield, FiLock, FiMail, FiSmartphone } = FiIcons;

export default function BlinkLoginModal({ onClose, onLoginSuccess }) {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'pin'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Temp storage for 2FA flow
  const [authContext, setAuthContext] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await blinkService.login(email, password);
      
      if (result.status === 'verification_required') {
        setAuthContext(result);
        setStep('pin');
      } else {
        onLoginSuccess(result);
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await blinkService.verifyPin(
        authContext.accountId,
        authContext.clientId,
        authContext.authtoken,
        pin,
        authContext.region
      );
      
      onLoginSuccess(result);
      onClose();
    } catch (err) {
      setError(err.message || 'PIN ungültig');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm" onClick={onClose} />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        >
          <div className="bg-blue-600 p-6 text-center">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Blink Login</h2>
            <p className="text-blue-100 text-sm mt-1">Sichere Verbindung via Blink API</p>
          </div>

          <div className="p-6">
            {step === 'credentials' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input 
                      type="email" 
                      value={email} 
                      onChange={e => setEmail(e.target.value)}
                      className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input 
                      type="password" 
                      value={password} 
                      onChange={e => setPassword(e.target.value)}
                      className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                </div>
                {error && <div className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</div>}
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Verbinde...' : 'Weiter'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyPin} className="space-y-4">
                <div className="text-center mb-4">
                   <SafeIcon icon={FiSmartphone} className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                   <p className="text-sm text-gray-600">
                     Wir haben Ihnen einen PIN Code per Email oder SMS gesendet. Bitte geben Sie diesen ein.
                   </p>
                </div>
                <div>
                  <label className="block text-center text-sm font-medium text-gray-700 mb-2">PIN Code</label>
                  <input 
                    type="text" 
                    value={pin} 
                    onChange={e => setPin(e.target.value)}
                    className="w-full text-center text-2xl tracking-widest border border-gray-300 rounded-lg px-3 py-2 font-mono"
                    placeholder="0000"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>
                {error && <div className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</div>}
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Verifiziere...' : 'Bestätigen'}
                </button>
                <div className="text-center mt-2">
                    <button type="button" onClick={() => setStep('credentials')} className="text-xs text-blue-600 hover:underline">
                        Zurück
                    </button>
                </div>
              </form>
            )}
          </div>
          <button onClick={onClose} className="absolute top-2 right-2 text-white/80 hover:text-white">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </motion.div>
      </div>
    </div>
  );
}